/* jslint node: true */
/* jshint esversion: 6 */

//******************************************************************************
// Copyright © 2016-2018
// by Videojet Technologies, Inc.
// All Rights Reserved
//******************************************************************************
// @doc
//
// @module      ShiftChecker.js | Shift scheduling check functions
//
// @comm
//
// @devnote     "On line xyz, at Date Time (on a minute slice), are we in a shift?"
//              utilizing recurrence rules thru rfc-5545 style options
//
// @author      SBradley
//
// @date        2018/02/13
//
// @end
//******************************************************************************
// $Header$
//
// $Log$
//
//******************************************************************************


//*****************************************************************************
// Required libraries
//*****************************************************************************
import { RRule, RRuleSet } from 'rrule';
import { ConsoleLog } from './consoleLog';
import { ShiftOverlapFinder } from './shiftOverlapFinder';
import Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';
import { deepCopy } from './util/json';
import { isTimeInRanges } from './dateRangeChecker';
import { DateTime, Info, Settings } from 'luxon';
;

const logger = new ConsoleLog();
const moment = extendMoment(Moment);

logger.debug('ShiftChecker', Info.features());

//*****************************************************************************
//
// @func    Remove a list of chars throughout a string
//
// @rdesc   String with char list removed
//
// @ex      var s = name.removeChars("abc");
//
//*****************************************************************************
String.prototype.removeChars = function (p_chars) {
  const re = new RegExp(`[${p_chars}]`, 'g');
  return this.replace(re, '');
};

//*****************************************************************************
//
// @func    Format a Date object to be a proper RRule date string
//
// @rdesc   Manipulated string
//
// @ex      var s = dt.toRRuleDtStr();
//
//*****************************************************************************
Date.prototype.toRRuleDtStr = function () {
  let iso = this.toISOString()
    .removeChars('-:');
  if (iso.charAt(15) === '.') iso = `${iso.substring(0, 15)}Z`;
  return iso;
};
//*****************************************************************************
// @func    Clear time from Date obj
//*****************************************************************************
Date.prototype.withoutTime = function () {
  const d = new Date(this);
  d.setHours(0, 0, 0, 0);
  return d;
};
//*****************************************************************************
// @func    Get date/time text as 'YYYYMMDDHHMMSS'
//*****************************************************************************
Date.prototype.getDiff = function (p_dateTime) {
  const t = this.valueOf() - p_dateTime.valueOf();
  return t;
};
//*****************************************************************************
// Adds time to a date. Modelled after MySQL DATE_ADD function.
// Example: dtObj.add('minute', 30)  (returns 30 minutes from now.)
// https://stackoverflow.com/a/1214753/18511
//
// @param interval  One of: year, quarter, month, week, day, hour, minute, second, mils
// @param units  Number of units of the given interval to add.
//*****************************************************************************
Date.prototype.add = function (interval, units) {
  let ret = new Date(this); // don't change original date
  switch (interval.toLowerCase()) {
    case 'year':
      ret.setFullYear(ret.getFullYear() + Number(units));
      break;
    case 'quarter':
      ret.setMonth(ret.getMonth() + 3 * units);
      break;
    case 'month':
      ret.setMonth(ret.getMonth() + Number(units));
      break;
    case 'week':
      ret.setDate(ret.getDate() + 7 * units);
      break;
    case 'day':
      ret.setDate(ret.getDate() + Number(units));
      break;
    case 'hour':
      ret.setTime(ret.getTime() + units * 3600000);
      break;
    case 'minute':
      ret.setTime(ret.getTime() + units * 60000);
      break;
    case 'second':
      ret.setTime(ret.getTime() + units * 1000);
      break;
    case 'mils':
      ret.setTime(ret.getTime() + Number(units));
      break;
    default:
      ret = undefined;
      break;
  }
  return ret;
};

//*****************************************************************************
// @func Check for valid shift data for RRule creation
//*****************************************************************************
function isValidForRRule(p_shift) {
  return (p_shift.recurrenceRule && p_shift.recurrenceRule.length > 0);
}

const rangify = event => moment.range(event.instanceInfo.startTime, event.instanceInfo.endTime);

/*
We create an RRule in site local start time (we store this in utc), so we convert UTC => site time*.
This site time is represented as JSDate constructed with a zero offset.
  So if a shift started in Feb at 0730 Phoenix time (MST).
  We’d save this as 2020-02-28T14:30:00.000Z , then convert this to 2020-02-28T07:30:00.000-07:00 and then finally to 2020-02-28T07:30:00.000Z before constructing the rule.
Instances returned from rrule will be in client local time. must be converted to UTC.
  So when I asked about occurrences around now I get this: 2020-08-25T10:30:00.000Z
  this is because my client tzid is America/New_York , and 0730 Phoenix is 1030  (EDT).
  Again this presented as a zero offset time, and we convert it back to UTC successfully (2020-08-25T14:30:00.000Z).
  If I was in Standard time I would ask around 0930
 These functions help transition between the different types of time
 utc - real utc time
 localUtc - site time with no zone info
 clientLocalUTc - client time with no zone info
*/


/**
 * convert a UTC ISO time string into Date that has no zone info, but is in the site local time
 * @param startTime {string} - ISO utc time ('2018-02-01T01:30:00.000Z')
 * @param siteTz {string} - tzid ('Asia/Tokyo')
 * @returns {Date} - site local Date with utc zone info
 */
const utcToSiteLocalUtc = (startTime, siteTz= 'utc') => DateTime.fromISO(startTime).setZone(siteTz).setZone('utc', { keepLocalTime: true }).toJSDate();

/**
 * convert a UTC ISO time string into Date that has no zone info, but is in the client local time
 * @param startTime {string} - ISO utc time ('2018-02-01T01:30:00.000Z')
 * @returns {Date} - Local Date with utc zone info
 */
const utcToClientLocalUtc = (startTime) => DateTime.fromISO(startTime).toLocal().setZone('utc', { keepLocalTime: true }).toJSDate();

/**
 * convert a local zoned Date into Date that has no zone info, but is in the client local time
 * @param date {Date} - Date in local time
 * @returns {Date} - Local Date with utc zone info
 */
const localToClientLocalUtc = (date) => DateTime.fromJSDate(date).setZone('utc', { keepLocalTime: true }).toJSDate();

/**
 * covert Date that has no zone info, but is in the client local time into utc time
 * @param date {Date} - Date in local with utc zone info
 * @returns {Date} - Date in utc time
 */
const clientLocalUtcToLocal = (date)  => DateTime.fromJSDate(date).toUTC().setZone('local', { keepLocalTime: true }).toJSDate();

//*****************************************************************************
// @class   Various functions to check against
//*****************************************************************************
export class ShiftChecker {
  //*****************************************************************************
  // @func    Constructor
  //*****************************************************************************
  constructor(p_shiftData, parent = undefined, config = {}) {
    logger.trace('%j', {
      fn: 'ShiftChecker.constructor',
      p_shiftData,
    });
    this.shiftRules = [];
    this.shiftData = ShiftChecker.recurStringsEmpty(p_shiftData);
    this.rruleSet = new RRuleSet();
    this.siteTz = (p_shiftData.find(shift => shift.tz) || {}).tz;
    this.parent = parent;

    const { logLevel } = config;
    if (logLevel) logger.setLogLevel(logLevel);

    for (const shift of this.shiftData) {
      try {
        const rule = this.buildRule(shift);
        this.rruleSet.rrule(rule);
        this.shiftRules.push(rule);
      } catch (e) {
        logger.error('%j', { fn: 'ShiftChecker.constructor', ...e });
      }
    }
  }

  static recurStringEmpty(item) {
    return (item === undefined || item === null || item === 'NONE' || item === 'Never') ? '' : item;
  }

  /**
   * Translate recurrance strings from defaults to empty strings, if needed
   */
  static recurStringsEmpty(items) {
    const localItems = [];
    // eslint-disable-next-line guard-for-in,prefer-const
    for (const item of items) {
      item.recurrenceRule = ShiftChecker.recurStringEmpty(item.recurrenceRule);
      item.recurrenceException = ShiftChecker.recurStringEmpty(item.recurrenceException);
      localItems.push(item);
    }
    return localItems;
  }

  //*****************************************************************************
  // @func    Build an RRule from a shift data
  //*****************************************************************************
  buildRule(p_shift) {
    const { start, duration } = ShiftChecker.getDurationFromShift(p_shift);
    p_shift.recurrenceRule = ShiftChecker.recurStringEmpty(p_shift.recurrenceRule);
    p_shift.recurrenceException = ShiftChecker.recurStringEmpty(p_shift.recurrenceException);
    const options = RRule.parseString(isValidForRRule(p_shift) ? p_shift.recurrenceRule : 'FREQ=DAILY;COUNT=1');
    options.dtstart = start.toDate();
    const dtstart = utcToSiteLocalUtc(p_shift.startTime, p_shift.tz);
    // use parent shiftRules to benefit from rrule caching
    const exists = this.parent && this.parent.shiftRules.find(sr => sr.id === p_shift.id);

    if(exists) return exists;

    const rule = new RRule(({ ...options, tzid: p_shift.tz, dtstart: dtstart }));
    rule.itemid = p_shift.id;
    rule.id = p_shift.id;
    rule.duration = duration.toString();
    rule.startHours = start.hours();
    return rule;
  }

  //*****************************************************************************
  // @func    Check if a date/time string is within a shift period (for testing)
  //*****************************************************************************
  isMinuteWithinShift(p_dateTimeStr) {
    const shifts = this.shiftsAtMinute(p_dateTimeStr);
    return (shifts && shifts.length > 0);
  }

  static flatten(a) {
    return Array.isArray(a) ? [].concat(...a.map(ShiftChecker.flatten)) : a;
  }

  /**
   * Deep copy a shift object
   * @param event
   * @returns {any}
   */
  static copy(event) {
    return {
      ...deepCopy(event),
      instanceInfo: {
        startTime: moment.utc(event.instanceInfo.startTime),
        endTime: moment.utc(event.instanceInfo.endTime),
      },
    };
  }

  static getDurationFromShift(scheduledEvent) {
    const start = moment(scheduledEvent.startTime)
      .utc();
    const end = moment(scheduledEvent.endTime)
      .utc();

    if (!start.isValid() || !end.isValid()) {
      const e = new Error('getDurationFromShift shift data');
      e.data = scheduledEvent;
      throw e;
    }
    let duration = moment.duration(end.diff(start));
    duration = duration.asMilliseconds();

    //
    return {
      start,
      end,
      duration
    };
  }

  static createEventInstance(dt, duration) {
    return {
      startTime: moment.utc(dt),
      endTime: moment.utc(dt).add(duration, 'ms'),
    };
  }

  static getInstanceStart(scheduleItem) {
    return scheduleItem.instanceInfo
      ? scheduleItem.instanceInfo.startTime
      : moment.utc(scheduleItem.startTime);
  }

  static getInstanceEnd(scheduleItem) {
    return scheduleItem.instanceInfo
      ? scheduleItem.instanceInfo.endTime
      : moment.utc(scheduleItem.endTime);
  }

  //*****************************************************************************
  // @func    Lookup a shift by id (from original shift data)
  //*****************************************************************************
  getShiftData(dt, duration, itemid, atInstant = false) {
    for (let i = 0; i < this.shiftData.length; i++) {
      if (itemid === this.shiftData[i].id) {
        const item = deepCopy(this.shiftData[i]);
        if (atInstant) {
          item.instanceInfo = ShiftChecker.createEventInstance(moment.utc(dt), duration);
          logger.debug('%j', { dt, item, method: `ShiftChecker.${this.getShiftData.name}` });
        }
        return item;
      }
    }
    return null;
  }

  /**
   * Return all shifts which overlap a minute
   * @param {string|Date|moment.Moment} p_dateTimeStr
   * @returns {T[]}
   */
  shiftsAtMinute(p_dateTimeStr) {
    const chkTime = moment.utc(p_dateTimeStr);
    const beginTime = chkTime.clone()
      .subtract(1, 'd');// ?
    const endTime = chkTime.clone()
      .add(1, 'd');// ?

    logger.debug('%j', {
      message: 'calculated times',
      chkTime,
      beginTime,
      endTime,
      method: `ShiftChecker.${this.shiftsAtMinute.name}`
    });

    const test = this.shiftRules.map(sr => sr.toString());
    const shifts = this.shiftRules
      .map((sr) => {
        const occurrences = sr.between(localToClientLocalUtc(beginTime.toDate()), localToClientLocalUtc(endTime.toDate()), true)
          .map((dt) => {

            // RRule returns a date that is UTC representation of local time, so if a UTC start time is 09:00 (and started in standard) and the tz is EST (-5 UTC)
            // you will get back 04:00 UTC that represents 04:00 EST
            const tzAdjusted = clientLocalUtcToLocal(dt);
            // tzAdjusted should be in local now representing the UTC start time (04:00 EST (-5 UTC) + 5 => 09:00 UTC)
            const rrBegin = moment.utc(tzAdjusted);
            const rrEnd = rrBegin.clone().add(sr.duration, 'ms');
            logger.debug('%j', {
              message: 'mapping occurrences',
              dt,
              tzAdjusted,
              rrBegin,
              rrEnd,
              method: `ShiftChecker.${this.shiftsAtMinute.name}`
            });
            return chkTime.isBetween(rrBegin, rrEnd, null, '[)')
              ? this.getShiftData(tzAdjusted, sr.duration, sr.itemid, true)
              : undefined;
          })
          .filter(o => o);

        return occurrences && occurrences.length > 0 && occurrences[0]; // if we have anything, return it
      })
      .filter(s => s); // get rid of null records

    logger.debug('%j', {
      message: 'returning',
      shifts,
      method: `ShiftChecker.${this.shiftsAtMinute.name}`
    });

    return shifts;
  }

  /**
   * Return all shifts with start times that are included between stDt and endDt
   * @param {string|moment.Moment} stDt
   * @param {string|moment.Moment} endDt
   * @returns {{instances: *}[]}
   */
  shiftsDuring(stDt, endDt) {
    // make an array from the reduced values object
    logger.debug('getting shifts for a given time range', {fn: 'shiftsDuring', startDate: stDt, endDate: endDt });

    const shifts = this.shiftRules
      .map((sr) => {
        //This currently also returns an occurrences thats start on the endDt - I dont know if this is correct or not
        const startDate = (typeof stDt === 'string') ? moment.utc(stDt).toDate() : stDt.toDate();
        const startLocalUTC = localToClientLocalUtc(startDate);
        const endDate = (typeof endDt === 'string') ? moment.utc(endDt).toDate() : endDt.toDate();
        const endLocalUTC = localToClientLocalUtc(endDate);
        const occurrencesRaw = sr.between(startLocalUTC, endLocalUTC,true);
        const occurrences = occurrencesRaw.map(s => Object.assign({}, this.getShiftData(clientLocalUtcToLocal(s), sr.duration, sr.itemid, true)));
//
        //Only return the instance if we have an occurrence
        if (occurrences && occurrences.length > 0 ) {
          return {
            ...occurrences[0],
            instances: occurrences.map(o => o.instanceInfo)
          };
       }
      })
      .filter(s => s); // get rid of null records;

    logger.debug('returning shifts', {fn: 'shiftsDuring', shiftCount: shifts && shifts.length, shifts});
    return shifts || [];
  }

  /**
   * Return all shifts that begin or end between stDt and endDt
   * @param {string|Date|moment.Moment} stDt
   * @param {string|Date|moment.Moment} endDt
   * @returns {T}
   */
  shiftsOverlap(stDt, endDt) {
    const chkStart = moment.utc(stDt);
    const chkEnd = moment.utc(endDt);
    const chkRange = moment.range(chkStart, chkEnd);
    const beginTime = chkStart.clone()
      .subtract(1, 'd');
    const endTime = chkEnd.clone()
      .add(1, 'd');
    logger.trace('%j', {
      message: 'getting shifts which overlap a given time range',
      chkRange,
      method: `ShiftChecker.${this.shiftsOverlap.name}`
    });
    const shifts = ShiftChecker.flatten(this.shiftRules
      .map((sr) => {
        logger.trace('%j', {
          message: 'shift rule',
          rule: sr.id,
          duration: sr.duration,
          starthrs: sr.startHours,
          method: `ShiftChecker.${this.shiftsOverlap.name}`
        });
        const occurrences = sr.between(localToClientLocalUtc(beginTime.toDate()), localToClientLocalUtc(endTime.toDate()), false)
          .map((dt) => {
            const tzAdjusted = clientLocalUtcToLocal(dt);
            const rrBegin = moment.utc(tzAdjusted);
            const shiftRange = moment.range(rrBegin, rrBegin.clone().add(sr.duration, 'ms'));
            logger.debug('%j', {
              message: 'occurrence',
              tzAdjusted,
              rule: sr.id,
              duration: sr.duration,
              starthrs: sr.startHours,
              shiftRange,
              method: `ShiftChecker.${this.shiftsOverlap.name}`
            });
            if (chkRange.intersect(shiftRange)) {
              return Object.assign({}, this.getShiftData(tzAdjusted, sr.duration, sr.itemid, true));
            }
            return undefined;
          })
          .filter(o => o);

        return occurrences;
      })
      .filter(s => s.length > 0));
    const reduced = shifts.reduce((acc, itm) => {
      const found = acc.find(i => (i.id === itm.id && itm.instanceInfo.startTime.isSame(i.instanceInfo.startTime) && itm.instanceInfo.endTime.isSame(i.instanceInfo.endTime)));
      if (!found) acc.push(itm);
      return acc;
    }, []);
    logger.debug('%j', {
      message: 'returns',
      reduced,
      method: `ShiftChecker.${this.shiftsOverlap.name}`
    });
    return reduced;
  }

  /**
   * Get any shift overlapping the given time or the previous one
   * @param {string|Date|moment.Moment} isotime
   * @returns {T[]}
   */
  currentOrPast(isotime) {
    logger.trace('%j', {
      message: `getting shifts for isotime`,
      isotime,
      method: `ShiftChecker.${this.currentOrPast.name}`
    });

    const shifts = this.shiftRules
      .map((sr) => {
        const occurrence = sr.before(localToClientLocalUtc(moment.utc(isotime).toDate()), true);
        if (occurrence) {
        const tzAdjusted = clientLocalUtcToLocal(occurrence);
          const item = this.getShiftData(tzAdjusted, sr.duration, sr.itemid, true);
          if (item !== null) {
            return item;
          }
        }
      })
      .filter(f => f);

    logger.debug('%j', {
      message: 'returns',
      shifts,
      method: `ShiftChecker.${this.currentOrPast.name}`
    });
    return shifts;
  }

  /**
   *
   * @param {scheduleTypes} scheduleType - type to check for overlap
   * @param {object} shiftItemToValidate - item to check overlap with
   * @returns {array} returns true if there is no overlapping
   */
  getOverlappingSchedulesByType(scheduleType, shiftItemToValidate) {
    if (shiftItemToValidate.lineIds == null || shiftItemToValidate.lineIds.length === 0) {
      return true;
    }

    const ruleToCheck = this.buildRule(Object.assign({}, shiftItemToValidate));
    logger.debug('new rule:', { fn: this.getOverlappingSchedulesByType.name, shiftItemToValidate });
    const otherShifts = this.shiftData.filter(data => data.id !== shiftItemToValidate.id);
    if (Array.isArray(otherShifts) && otherShifts.length > 0) {
      const shiftsWithMatchingLineIds = otherShifts.filter((shift) => {
        if ((Array.isArray(shift.lineIds) && shift.lineIds.length > 0)) {
          return shift.lineIds.filter(id => shiftItemToValidate.lineIds.includes(id)).length > 0;
        }
      });
      try {
        const overlappingShifts = shiftsWithMatchingLineIds.filter((data) => {
          logger.debug('checking:', { fn: this.getOverlappingSchedulesByType.name, data });
          const rule = this.buildRule(Object.assign({}, data));
          return new ShiftOverlapFinder(ruleToCheck, rule).run();
        });
        return (overlappingShifts.length > 0) ? overlappingShifts : false;
      } catch (e) {
        logger.error('failed to find overlap', { fn: this.getOverlappingSchedulesByType.name, e });
        throw e;
      }
    }
    return undefined;
  }

  /**
   *
   * @param {[object]} events
   * @param {number} scheduleTypeId - type to check for overlap
   * @param LineIds
   * @returns {*}
   */
  getEventsByLineIds(events, scheduleTypeId, LineIds = []) {
    if (!events) return undefined;
    let returnArray = [];
    if (!LineIds || LineIds.length < 1) {
      returnArray = events.filter(e => e.typeId === scheduleTypeId);
    } else {
      returnArray = events.filter(e => e.typeId === scheduleTypeId && e.lineIds.some(l => LineIds.indexOf(l) >= -1));
      // returnArray = events.filter(e => e.typeId === scheduleType.value && e.lineIds.includes(LineIds));
    }
    return returnArray;
  }

  static defaultInstance(scheduledEvent) {
    return {
      startTime: moment(scheduledEvent.startTime)
        .utc(),
      endTime: moment(scheduledEvent.endTime)
        .utc(),
    };
  }

  setConcurrentEvents(scheduledEvents) {
    for (const event of scheduledEvents) {
      if (!event.instanceInfo) {
        event.instanceInfo = ShiftChecker.defaultInstance(event);
      }
      const shifts = this.shiftsDuring(event.instanceInfo.startTime, event.instanceInfo.endTime);
      // return instanced events that start before event's end time and not this event
      event.instanceInfo.concurrentEvents = (shifts
        .filter(e => e.instances.length > 0 && e.id !== event.id && e.typeId != event.typeId && event.instanceInfo) || [])
        .sort();

      logger.debug('%j', { event, method: `ShiftChecker.${this.setConcurrentEvents.name}` });
    }
  }

  /**
   * Generate a segmented schedule timeline, on every transition.
   * E.g. for breaks contained within shifts, it creates shift segments on either side of the break
   * @param scheduleEvents
   * @returns {this|*[]}
   */
  static getSequentialSegments(scheduleEvents) {
    logger.info('%j', {
      message: `getting sequential segments`,
      scheduleEvents,
      method: `ShiftChecker.${this.getSequentialSegments.name}`
    });

    if (!scheduleEvents.length) return [];

    const segments = [];

    for (const shift of scheduleEvents) {
      if (shift.instanceInfo.concurrentEvents && shift.instanceInfo.concurrentEvents.length) {
        // split shift by concurrent events
        logger.debug('%j', {
          message: 'Splitting events',
          title: shift.title,
          count: shift.instanceInfo.concurrentEvents.length,
          method: `ShiftChecker.${this.getSequentialSegments.name}`,
        });
        const shiftRanges = [rangify(shift)];
        for (const subevent of shift.instanceInfo.concurrentEvents) {
          const subrange = rangify(subevent);
          // find the piece of shift containing concurrency
          const idx = shiftRanges.findIndex(sr => sr.overlaps(subrange));
          if (idx >= 0) {
            // yank the piece of shift and split by inverted intersection
            const [superRange] = shiftRanges.splice(idx, 1);
            shiftRanges.push(...superRange.subtract(subrange));
            // include concurrent in segments
            segments.push(subevent);
          } else {
            logger.warn('%j', {
              message: 'No range containing current event',
              subevent,
              method: `ShiftChecker.${this.getSequentialSegments.name}`
            });
          }
        }

        // remake schedule events from ranges
        segments.push(...shiftRanges.map(sr => ({
          ...shift,
          concurrentEvents: undefined,
          instanceInfo: {
            startTime: sr.start.utc(),
            endTime: sr.end.utc(),
          },
        })));
      } else {
        logger.debug('%j', {
          message: 'No concurrent events',
          shift,
          method: `ShiftChecker.${this.getSequentialSegments.name}`,
        });
        segments.push(shift);
      }
    }

    const sortedSegments = segments.sort((a, b) => a.instanceInfo.startTime - b.instanceInfo.startTime);
    logger.debug('%j', {
      message: 'returning sorted segments',
      sortedSegments,
      method: `ShiftChecker.${this.getSequentialSegments.name}`,
    });
    return sortedSegments;
  }

  getRRuleSet() {
    return this.rruleSet;
  }

  static getLineAutoStandbyShift(shift, autoStandbyEvent){
    if(shift && shift.instanceInfo){
      const ended = autoStandbyEvent.endDt != null;
      return ({...shift,
        instanceInfo: ({
          ...shift.instanceInfo,
          ...(!ended ? { autoStandbyActive: true } : {}),
          startTime: (ended
            ? moment.utc(autoStandbyEvent.endDt)
            : shift.instanceInfo.startTime)
        })
      })
    }

    return shift;
  }

  /**
   *
   * @param {moment.Moment} shiftStart is the instanced shift start
   * @param {moment.Moment} timeMoment is the time into the shift
   */
  static getStartTime(shiftStart, timeMoment) {
    if ( timeMoment.clone().startOf('hour').isAfter(shiftStart) ) {
      return timeMoment.clone().startOf('hour').startOf('minute');
    } else {
      return shiftStart.clone().startOf('minute');
    }
  }

  /**
   *
   * @param {moment.Moment} shiftEnd is the instanced shift end
   * @param {moment.Moment} timeMoment is the time into the shift
   * @param {boolean } max find the latest
   */
  static getEndTime(shiftEnd, timeMoment, max) {

    if(max) {
      const endOfHour = timeMoment.clone().endOf('hour').startOf('minute');
      if(shiftEnd.isBefore(endOfHour)) {
        // get the previous to end minute (ends are exclusive)
        return shiftEnd.clone();
      }
      else {
        return endOfHour.clone().add(1, 'minute');
      }
    } else {
      if (shiftEnd.isSameOrBefore(timeMoment)) {
        return shiftEnd.clone();
      } else {
        return timeMoment.clone().startOf('minute').add(1, 'minute');
      }
    }
  }
  /**
   *
   * @param shift
   * @param {moment.Moment} timeMoment is the time into the shift
   * @param {boolean } max find the latest
   * @param autoStandbyEvent
   * @param lineId line guid to get the scheduled run minute for
   * @param plannedDowntimes downtime marked with planned reason
   * @returns {number} scheduled minutes for time in shift
   */
  static getScheduledRunMinutes2({ shift, timeMoment, max = false, autoStandbyEvent, lineId, plannedDowntimes = [] }) {
    logger.info('start', {
      fn: ShiftChecker.getScheduledRunMinutes2.name, autoStandbyEvent,
      lineId, max, shift, timeMoment: timeMoment.format() });

    if (shift == null || shift.instanceInfo == null || shift.instanceInfo.startTime == null || shift.instanceInfo.endTime == null) {
      logger.error('shift is null', {
        fn: ShiftChecker.getScheduledRunMinutes2.name, shift});
      return 0;
    }

    //Return 0 if this line is not in this shift
    if (shift.lineIds == null || !shift.lineIds.includes(lineId)) {
      logger.error('lineId is not in shift', {
        fn: ShiftChecker.getScheduledRunMinutes2.name, shift, lineId});
      return 0;
    }

    const timeOfInterest = ShiftChecker.getEndTime(shift.instanceInfo.endTime, timeMoment, max);
    let startTimeOfInterest = ShiftChecker.getStartTime(shift.instanceInfo.startTime, timeMoment);

    if(autoStandbyEvent) {
      const endMoment = moment.utc(autoStandbyEvent.endDt);
      if (autoStandbyEvent.endDt == null || timeOfInterest.isBefore(endMoment)) {
        logger.debug('in an active autoStandbyEvent', {
          fn: ShiftChecker.getScheduledRunMinutes2.name, shift, autoStandbyEvent, timeMoment: timeMoment.format() });
        return 0;
        //Only update the start time if the auto standby event ended this hour
      } else if ((endMoment != null) && (endMoment.isAfter(startTimeOfInterest)) ){
        startTimeOfInterest = endMoment;
      }
    }

    let scheduledRunMinutes = 0;

    //Get any concurrent events for this lineId
    const concurrentEvents = shift.instanceInfo && shift.instanceInfo.concurrentEvents &&
      shift.instanceInfo.concurrentEvents.filter(ce => ce.lineIds.includes(lineId)) || [];

    const instancedConcurrentEvents = concurrentEvents.map( ce => {
      if (ce.instanceInfo && ce.instanceInfo.startTime && ce.instanceInfo.endTime && ce.typeId > 1){
        return ({
          startTime: ce.instanceInfo.startTime,
          endTime: ce.instanceInfo.endTime
        })
      }
    });

    const nonRunEvents = instancedConcurrentEvents.concat(...plannedDowntimes.map(dt => ({
      startTime: moment.utc(dt.startDt),
      endTime: moment.utc(dt.endDt),
    })));

    if (nonRunEvents && nonRunEvents.length > 0) {
      for (let m = moment(startTimeOfInterest); m.isBefore(timeOfInterest); m.add(1, 'minute')) {
        if (!isTimeInRanges(m, nonRunEvents)) {
          scheduledRunMinutes++;
        }
      }
    } else {
      scheduledRunMinutes = timeOfInterest.diff(startTimeOfInterest, 'minutes');
    }

    if (scheduledRunMinutes < 0 || scheduledRunMinutes > 60) {
      logger.error('invalid schedule run minutes', {
        fn: ShiftChecker.getScheduledRunMinutes2.name, startTimeOfInterest: startTimeOfInterest.format(),
        timeOfInterest: timeOfInterest.format(), timeMoment: timeMoment.format(), shift, max, autoStandbyEvent, lineId });

      if (scheduledRunMinutes < 0) {
        scheduledRunMinutes = 0;
      } else {
        scheduledRunMinutes = 60;
      }
    }
    logger.debug('end', {
      fn: ShiftChecker.getScheduledRunMinutes2.name, timeMoment: timeMoment.format(), scheduledRunMinutes });

    return scheduledRunMinutes;

  }

  inferSiteTz() {
    return this.siteTz;
  }
}

//*****************************************************************************
// Module export
//*****************************************************************************
